<template>
  <b-modal
    id="screenshot-viewer-modal"
    modal-class="screenshot-viewer-modal"
    centered
    size="lg"
    hide-footer
    v-model="internalShow"
    @hide="handleHide"
  >
    <template #modal-header>
      <div class="screenshot-viewer-header text-center">
        <h4>Screenshot Viewer</h4>
        <b-button variant="close-btn" class="btn-container" @click="closeModal">
          <i class="fas fa-times"></i>
        </b-button>
      </div>
    </template>

    <b-container>
      <div class="screenshot-chips d-flex flex-wrap mb-3 gap-2">
        <b-badge
          v-for="(screenshot, index) in screenshots"
          :key="index"
          pill
          variant="light"
          class="screenshot-chip"
          :class="{ active: activeScreenshot === index }"
          @click="activeScreenshot = index"
        >
          {{ screenshot.name || `Screenshot ${index + 1}` }}
        </b-badge>
      </div>
      <div class="screenshot-display text-center" @click="toggleZoom">
        <img
          v-if="screenshots.length > 0"
          :src="screenshots[activeScreenshot].url"
          :alt="screenshots[activeScreenshot].name"
          class="screenshot-img"
        />
        <p v-else class="text-muted text-center mt-4">
          No screenshots available for this enrollment.
        </p>
      </div>
      <div v-if="isZoomed" class="zoom-overlay" @click="toggleZoom">
        <img
          :src="screenshots[activeScreenshot].url"
          :alt="screenshots[activeScreenshot].name"
          class="zoomed-img"
        />
      </div>
      <div v-if="screenshots.length > 1" class="screenshot-action-btns">
        <b-button
          class="screenshot-action-btn btn"
          :disabled="activeScreenshot === 0"
          @click="prevScreenshot"
        >
          <i class="fas fa-chevron-left mr-2"></i> Previous
        </b-button>

        <span class="text-muted screenshot-pagination">
          {{ activeScreenshot + 1 }} of {{ screenshots.length }}
        </span>

        <b-button
          class="screenshot-action-btn btn btn-primary"
          :disabled="activeScreenshot === screenshots.length - 1"
          @click="nextScreenshot"
        >
          Next <i class="fas fa-chevron-right ml-2"></i>
        </b-button>
      </div>
    </b-container>
  </b-modal>
</template>

<script>
export default {
  name: "ScreenshotViewerModal",
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    screenshotsData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      internalShow: false,
      activeScreenshot: 0,
      screenshots: [],
      isZoomed: false,
    };
  },
  watch: {
    show(val) {
      this.internalShow = val;
      if (val) {
        this.screenshots = [...this.screenshotsData];
        this.activeScreenshot = 0;
      }
    },
    screenshotsData: {
      handler(newVal) {
        this.screenshots = [...newVal];
        this.activeScreenshot = 0;
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    toggleZoom() {
      this.isZoomed = !this.isZoomed;
    },
    closeModal() {
      this.internalShow = false;
    },
    handleHide() {
      this.$emit("onCloseModal");
      this.screenshots = [];
      this.activeScreenshot = 0;
    },
    nextScreenshot() {
      if (this.activeScreenshot < this.screenshots.length - 1) {
        this.activeScreenshot++;
      }
    },
    prevScreenshot() {
      if (this.activeScreenshot > 0) {
        this.activeScreenshot--;
      }
    },
  },
};
</script>
<style>
#screenshot-viewer-modal___BV_modal_content_ .modal-header {
  padding: 1rem 0 0 0;
}
</style>
<style scoped>

.screenshot-viewer-modal {
  max-height: 90vh;
  overflow-y: auto;
}

.screenshot-chip {
  cursor: pointer;
  user-select: none;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  transition: all 0.2s ease;
  color: #1e1e1e;
  border: none;
  border-radius: 2rem;
  font-weight: 400;
  background-color: #f0f0f0;
}

.screenshot-chip:hover {
  background-color: #e6faff;
  color: #028cb0;
  border-color: #028cb0;
}

.screenshot-chip:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(3, 195, 236, 0.3);
}

.screenshot-chip.active {
  background-color: #03c3ec !important;
  color: white;
  border-color: #03c3ec;
}

.screenshot-chip.disabled,
.screenshot-chip[disabled] {
  pointer-events: none;
  background-color: #f0f0f0;
  color: #aaa;
  border-color: #ddd;
}

.screenshot-display {
  width: 100%;
  max-width: 100%;
  height: 450px;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #eee;
  border-radius: 6px;
  overflow: hidden;
}

.screenshot-img {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
  cursor: zoom-in;
}

.zoom-overlay {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1050;
  background-color: rgba(0, 0, 0, 0.9);
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
}

.zoomed-img {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.4);
}

/* btns wrapper */
.screenshot-action-btns {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
}
.screenshot-action-btns .screenshot-pagination {
  text-align: center;
  margin: auto;
  font-weight: 500;
}
/* individual btns */
.screenshot-action-btn {
  max-width: 300px;
  width: 100%;
  color: #03c3ec;
  background-color: #fff;
  border: solid 1px #03c3ec;
  /* border-radius: 1rem; */
  transition: all 0.2s ease;
  cursor: pointer;
}

.screenshot-action-btn:hover {
  background-color: #03c3ec;
  color: #fff;
  box-shadow: 0 2px 6px rgba(3, 195, 236, 0.3);
}

.screenshot-action-btn:active {
  background-color: #029ec4;
  color: #fff;
  border-color: #029ec4;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.screenshot-action-btn:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(3, 195, 236, 0.3);
}

.screenshot-action-btn:disabled {
  background-color: #f1f1f1;
  color: #aaa;
  border-color: #ccc;
  cursor: not-allowed;
  box-shadow: none;
}
@media (max-width: 992px) {
    .screenshot-display {
        height: 250px;
    }
  /* btns wrapper */
  .screenshot-action-btns {
    flex-direction: column;
    justify-content: center;
    gap: 0.5rem;
  }
  .screenshot-action-btn {
    max-width: none;
  }
}
@media (max-width: 768px) {
    .screenshot-chip {
        /* max-width: 50%; */
        width: 100%;
        padding: 0.75rem 1rem;
    }
}
@media (max-width: 576px) {
    .screenshot-display {
        height: 250px;
    }
}
</style>
